<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import kpiImage from "@/assets/imgkpi.webp";
import kpiImageSmall from "@/assets/imgkpi-sm.webp";

const props = defineProps({
  items: {
    type: Array,
    default: () => [
      {
        id: 1,
        name: "Clientes Ativos",
        value: "500+",
        descBold: "Empresas",
        descRest: " simplificam a sua gestão.",
      },
      {
        id: 2,
        name: "Notas Fiscais",
        value: "150000+",
        descBold: "Notas fiscais",
        descRest: " emitidas por mês.",
      },
      {
        id: 3,
        name: "Satisfação",
        value: "99%",
        descBold: "Satisfação média",
        descRest: " entre nossos clientes.",
      },
    ],
  },
  durationMs: {
    type: Number,
    default: 2400,
  },
  staggerMs: {
    type: Number,
    default: 220,
  },
});

const statsSectionRef = ref(null);
const animatedStats = ref([]);
const inView = ref(false);
const hasAnimated = ref(false);

function parseStatValue(raw) {
  const str = String(raw ?? "");
  const match = str.match(/^(\d+)/);
  const target = match ? parseInt(match[1], 10) : 0;
  const suffix = match ? str.slice(match[1].length) : str;
  return { target, suffix };
}

function computeEasedValue(start, end, elapsedMs, durationMs) {
  const duration = durationMs > 0 ? durationMs : 1;
  const clamped = Math.min(Math.max(elapsedMs / duration, 0), 1);
  const eased = 1 - Math.pow(1 - clamped, 3);
  return Math.floor(start + eased * end);
}

function buildAnimatedStats() {
  animatedStats.value = props.items.map((s) => {
    const parsed = parseStatValue(s.value);
    return {
      ...s,
      target: parsed.target,
      suffix: parsed.suffix,
      displayed: 0,
    };
  });
}

function animateStat(stat, delayMs = 0) {
  setTimeout(() => {
    const start = 0;
    const end = stat.target;
    const startTime = performance.now();
    const duration = props.durationMs;
    const step = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      stat.displayed = computeEasedValue(start, end, elapsed, duration);
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        stat.displayed = end;
      }
    };
    requestAnimationFrame(step);
  }, delayMs);
}

function animateStats() {
  animatedStats.value.forEach((stat, idx) => {
    stat.displayed = 0;
    animateStat(stat, idx * props.staggerMs);
  });
}

function formatNumber(n) {
  try {
    return n.toLocaleString("pt-BR");
  } catch {
    return String(n);
  }
}

let kpiObserver = null;

onMounted(() => {
  buildAnimatedStats();
  if (typeof IntersectionObserver !== "undefined") {
    kpiObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target !== statsSectionRef.value) return;
          if (entry.isIntersecting) {
            inView.value = true;
            if (!hasAnimated.value) {
              hasAnimated.value = true;
              animateStats();
            }
          } else {
            inView.value = false;
          }
        });
      },
      { threshold: 0.25 },
    );
    if (statsSectionRef.value) kpiObserver.observe(statsSectionRef.value);
  } else {
    inView.value = true;
    animateStats();
  }
});

onBeforeUnmount(() => {
  if (kpiObserver) {
    kpiObserver.disconnect();
    kpiObserver = null;
  }
});
</script>

<template>
  <section
    class="relative py-12 bg-gradient-to-r via-white from-kali-blue-100 to-kali-blue-100 sm:py-16 lg:py-28"
  >
    <div class="px-6 mx-auto max-w-7xl lg:px-8" ref="statsSectionRef">
      <div
        class="relative grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)] items-start"
      >
        <div class="w-full lg:max-w-xl">
          <h2
            :class="[
              'text-4xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] sm:text-5xl leading-tight sm:leading-tight mb-2 transition-opacity duration-700 text-center lg:text-left',
              inView ? 'opacity-100' : 'opacity-0',
            ]"
          >
            Números que fazem a diferença
          </h2>
          <p
            :class="[
              'hidden lg:block mt-2 max-w-md text-base text-gray-700 sm:text-lg transition-opacity duration-700 lg:text-left',
              inView ? 'opacity-100' : 'opacity-0',
            ]"
          >
            Estes indicadores mostram como o Kali Gestão simplifica processos,
            reduz tarefas manuais e ajuda nossos clientes a alcançarem mais
            eficiência, segurança e previsibilidade na gestão do negócio.
          </p>
        </div>

        <div class="relative">
          <div
            :class="[
              'overflow-hidden bg-white rounded-3xl shadow-2xl border border-white/40',
              inView ? 'animate-fadeIn' : 'opacity-0',
            ]"
          >
            <div class="relative bg-black/10 aspect-[16/10]">
              <img
                :src="kpiImage"
                :srcset="`${kpiImageSmall} 600w, ${kpiImage} 1000w`"
                sizes="(min-width: 1024px) 700px, calc(100vw - 48px)"
                alt="Equipe utilizando as soluções Kali Gestão"
                class="block object-cover object-center absolute inset-0 w-full h-full kpi-photo"
                loading="lazy"
              />
            </div>
            <div
              class="px-6 py-6 text-white lg:hidden bg-kali-blue-600 sm:px-8"
            >
              <p
                class="text-base font-light leading-7 text-white/95 sm:text-lg sm:leading-8"
              >
                Estes indicadores mostram como o Kali Gestão simplifica
                processos, reduz tarefas manuais e ajuda nossos clientes a
                alcançarem mais eficiência, segurança e previsibilidade na
                gestão do negócio.
              </p>
            </div>
          </div>
        </div>

        <div
          class="mt-8 lg:mt-0 lg:absolute lg:left-0 lg:translate-x-0 lg:-bottom-10 lg:w-[75%]"
        >
          <div
            class="grid grid-cols-3 bg-white rounded-2xl border divide-x divide-gray-300 ring-1 shadow-xl border-gray-200/60 ring-black/5"
          >
            <div
              v-for="stat in animatedStats"
              :key="stat.id"
              class="flex flex-col gap-2 items-start px-4 py-4 min-w-0 h-full sm:gap-3 sm:px-6 sm:py-6"
            >
              <p
                v-if="stat.suffix === '+'"
                class="flex gap-1 justify-start items-baseline"
              >
                <span
                  class="text-2xl sm:text-5xl font-black leading-none tracking-tight tabular-nums text-transparent bg-clip-text bg-gradient-to-r from-kali-blue-500 via-[#006086] to-[#004d6b]"
                  >+</span
                >
                <span class="text-xs font-semibold sm:text-base text-slate-600"
                  >de</span
                >
                <span
                  class="ml-1 text-2xl sm:text-5xl font-black leading-none tracking-tight tabular-nums text-transparent bg-clip-text bg-gradient-to-r from-kali-blue-500 via-[#006086] to-[#004d6b]"
                  >{{ formatNumber(stat.displayed) }}</span
                >
              </p>
              <p
                v-else
                class="text-2xl sm:text-5xl font-black leading-none tracking-tight tabular-nums text-transparent bg-clip-text bg-gradient-to-r from-kali-blue-500 via-[#006086] to-[#004d6b] text-left"
              >
                {{ formatNumber(stat.displayed)
                }}<span
                  class="ml-1 text-base font-bold sm:text-2xl text-slate-700"
                  >{{ stat.suffix }}</span
                >
              </p>

              <p
                class="text-xs leading-snug text-left text-gray-700 sm:text-base"
              >
                <span class="font-semibold text-gray-900">{{
                  stat.descBold
                }}</span
                >{{ stat.descRest }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.kpi-photo {
  image-orientation: from-image;
  -webkit-image-orientation: from-image;
}
</style>
